import React, { FC, useEffect, useState } from 'react';
import { Button, Drawer, Form, notification, Select, Space, TimePicker } from 'antd';
import EmployeeInput from 'features/absence/madalAddOneAbsence/employeeInput';
import { useLazyGetDivisionEmployeesQuery } from 'app/services/edpApiV1/viewer';
import { TDivisionEmployees } from 'shared/types/types';
import { OvertimeCompensationType, OvertimeStatuses } from 'shared/enums/overtime';
import { PICKER_DATE_MASK, PICKER_TIME_MASK, SERVER_DATE_MASK } from 'shared/constants';
import { TOvertimeGetResponse, TUserOvertimeInfo } from 'shared/types/overtime.types';
import { OVERTIME_FORM_FIELDS, WORK_TIME_OVER_MESSAGE_OVERTIME } from 'shared/constants/overtime';
import { momentFormatCheck } from 'shared/helpers/type-check';
import { getPrettiedHoursAndMinutes } from 'shared/helpers/date';
import dayjs, { Dayjs } from 'dayjs';
import DatePickerWithDots from 'shared/components/datePickerWithDots/DatePickerWithDots';
import TimeRangePickerWithDots from 'shared/components/timeRangePickerWithDots/TimeRangePickerWithDots';
import style from './index.module.scss';

type TProps = {
  onClose: () => void;
  isOpen: boolean;
  onOk: (request: TUserOvertimeInfo) => void;
  currentOvertime?: TOvertimeGetResponse;
  currentUser?: TUserOvertimeInfo;
  remove?: (id: number | string) => void;
};

type TFormData = {
  type: OvertimeCompensationType;
  employeeId: number;
  isSecondBrake: boolean;
  toDate: Dayjs;
  workTime: [Dayjs, Dayjs];
  secondBrakeTime?: [Dayjs, Dayjs];
};

const OvertimeSidebar: FC<TProps> = ({ onClose, isOpen, onOk, currentOvertime, currentUser, remove }) => {
  const [form] = Form.useForm();
  const [employee, setEmployee] = useState<TDivisionEmployees | null>(null);
  const [getEmployees, resultEmployees] = useLazyGetDivisionEmployeesQuery();
  const [workDayDuration, setWorkDayDuration] = useState<string | null>(null);

  const submitForm = () => {
    // Убрана проверка т.к. она не должна работать для локального персонала
    // const workDayDurationHours = dayjs(workDayDuration, 'HH:mm').hour();
    // const workDayDurationMinutes = dayjs(workDayDuration, 'HH:mm').minute();
    // if (workDayDurationHours > 4 || (workDayDurationHours === 4 && workDayDurationMinutes > 0)) {
    //    notification.warning({ message: WORK_TIME_OVER_MESSAGE_OVERTIME });
    // } else {
    // }
    form.submit();
  };

  const calcDateDiff = (dates: unknown) => {
    if (!dates) {
      setWorkDayDuration(null);
      return;
    }
    const prettiedDate = getPrettiedHoursAndMinutes(dates as [Dayjs, Dayjs]);
    setWorkDayDuration(prettiedDate);
  };

  useEffect(() => {
    if (currentUser && currentOvertime && form) {
      form.setFieldValue('employeeId', currentUser.userInfo.id);
      form.setFieldValue('toDate', momentFormatCheck(currentUser.toDate));
      form.setFieldValue('type', currentUser.type);
      form.setFieldValue('workTime', [
        dayjs(currentUser.startTime, PICKER_TIME_MASK),
        dayjs(currentUser.endTime, PICKER_TIME_MASK),
      ]);
      calcDateDiff([dayjs(currentUser.startTime, PICKER_TIME_MASK), dayjs(currentUser.endTime, PICKER_TIME_MASK)]);
    }
  }, [currentUser, currentOvertime, form]);

  const onFormSubmitted = (formData: TFormData) => {
    const overtimeUser: TUserOvertimeInfo = {
      toDate: formData.toDate.format(SERVER_DATE_MASK),
      type: formData.type,
      userInfo: employee || currentUser!.userInfo,
      startTime: formData.workTime[0].format(PICKER_TIME_MASK),
      endTime: formData.workTime[1].format(PICKER_TIME_MASK),
      created: dayjs().format(SERVER_DATE_MASK),
    };

    if (currentUser) {
      overtimeUser.id = currentUser.id;
    }

    onOk(overtimeUser);
    form.resetFields(OVERTIME_FORM_FIELDS);
  };

  return (
    <Drawer
      title="Добавление сверхурочной работы"
      onClose={onClose}
      open={isOpen}
      width={720}
      extra={
        <Space>
          {remove ? <Button onClick={e => remove(currentUser?.id as number)}>Удалить</Button> : null}
          <Button onClick={onClose}>Отмена</Button>
          {currentOvertime?.status !== OvertimeStatuses.LOADED_CPRODUCT ? (
            <Button onClick={submitForm} type="primary">
              Ок
            </Button>
          ) : null}
        </Space>
      }
    >
      <Form
        form={form}
        name="basic"
        layout="vertical"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        style={{ maxWidth: 600 }}
        initialValues={{ remember: true }}
        onFinish={(data: TFormData) => onFormSubmitted(data)}
        autoComplete="off"
      >
        <EmployeeInput
          data={resultEmployees.data}
          getEmployees={getEmployees}
          setEmployee={setEmployee}
          divisionName={employee?.division}
          label="Выберите сотрудника"
          rules={[{ required: true, message: 'Выберите пользователя!' }]}
          className="form-item-overtime"
          onChangeEmployee={() => {}}
          defaultUser={currentUser?.userInfo}
          disabled={currentOvertime?.status === OvertimeStatuses.LOADED_CPRODUCT}
        />
        <div className={style['date-compensation']}>
          <Form.Item label="Дата" name="toDate" rules={[{ required: true, message: 'Выберите дату!' }]}>
            <DatePickerWithDots
              disabled={currentOvertime?.status === OvertimeStatuses.LOADED_CPRODUCT}
              format={PICKER_DATE_MASK}
              disabledDate={current => current.isBefore(dayjs(0))}
            />
          </Form.Item>
          <Form.Item
            name="type"
            label="Выберите тип компенсации"
            rules={[{ required: true, message: 'Выберите тип компенсации!' }]}
          >
            <Select disabled={currentOvertime?.status === OvertimeStatuses.LOADED_CPRODUCT}>
              <Select.Option value={OvertimeCompensationType.HOLIDAY}>{OvertimeCompensationType.HOLIDAY}</Select.Option>
              <Select.Option value={OvertimeCompensationType.MONEY}>{OvertimeCompensationType.MONEY}</Select.Option>
            </Select>
          </Form.Item>
        </div>
        <Form.Item
          name="workTime"
          className="form-item-overtime"
          label="Время работы"
          rules={[{ required: true, message: 'Введите время работы!' }]}
        >
          <TimeRangePickerWithDots
            disabled={currentOvertime?.status === OvertimeStatuses.LOADED_CPRODUCT}
            format={PICKER_TIME_MASK}
            placeholder={['Время начала работы', 'Время окончания работы']}
            style={{ width: '100%', maxWidth: '100%' }}
            onChange={calcDateDiff}
            needConfirm={false}
            changeOnScroll={false}
          />
        </Form.Item>
        {workDayDuration ? <p>Продолжительность рабочего дня: {workDayDuration}</p> : null}
      </Form>
    </Drawer>
  );
};

export default OvertimeSidebar;
